// Sprint 4c-3 C.2 — Mini-Matrix (2x2-Grid mit ICH + ZielFT Punkten + diagonaler Linie).
// 200x200 px, zwischen Frame-Text und 3 Sätzen auf Flexpfad-Seite.
//
// Quadranten-BG (leicht getönt) per Rolle:
//   Primary (own) #DCFCE7, Gegenüber #FEE2E2, Nachbarn #FEF3C7
// Diagonale Linie (gestrichelt) verbindet ICH und ZielFT.

import React from "react";
import { getRole, ROLE_COLORS } from "@/lib/focusRoles";

const FOKUSTYP_LABEL = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };

// Position der 4 FTs in der 2x2-Matrix (gleich wie DashboardTestResult).
// X-Achse: links = Sache, rechts = Mensch
// Y-Achse: oben = reizt mich, unten = funktioniert
const POSITIONS = {
  eule: { x: 25, y: 25 }, // top-left
  katze: { x: 75, y: 25 }, // top-right
  biene: { x: 25, y: 75 }, // bottom-left
  hund: { x: 75, y: 75 }, // bottom-right
};

// Quadrant-Mapping → welcher FT sitzt in welchem 2x2-Feld
const QUADRANT_SLUGS = [
  { slug: "eule", row: 0, col: 0 },
  { slug: "katze", row: 0, col: 1 },
  { slug: "biene", row: 1, col: 0 },
  { slug: "hund", row: 1, col: 1 },
];

export function MiniMatrix({ primarySlug, targetSlug, size = 200 }) {
  if (!primarySlug || !targetSlug) return null;

  const ichPos = POSITIONS[primarySlug];
  const zielPos = POSITIONS[targetSlug];
  if (!ichPos || !zielPos) return null;

  const ichRole = ROLE_COLORS.primary;
  const zielRole = ROLE_COLORS.gegenueber;

  return (
    <div
      data-testid="mini-matrix"
      data-primary={primarySlug}
      data-target={targetSlug}
      className="mx-auto"
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <div
        className="relative grid grid-cols-2 grid-rows-2 rounded-xl overflow-hidden border border-stone-200"
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        {/* 4 Quadrant-Hintergründe mit Role-Tints */}
        {QUADRANT_SLUGS.map(({ slug }) => {
          const role = getRole(primarySlug, slug);
          const bg = ROLE_COLORS[role].bg;
          return (
            <div
              key={slug}
              data-testid={`mini-matrix-quadrant-${slug}`}
              data-role={role}
              className="flex items-center justify-center"
              style={{ backgroundColor: bg }}
            >
              <span className="text-[10px] font-bold uppercase tracking-wider text-stone-700/70">
                {FOKUSTYP_LABEL[slug]}
              </span>
            </div>
          );
        })}

        {/* Achsenkreuz */}
        <div className="pointer-events-none absolute left-1/2 top-0 bottom-0 w-px bg-white/80" />
        <div className="pointer-events-none absolute top-1/2 left-0 right-0 h-px bg-white/80" />

        {/* Diagonale gestrichelte Linie ICH → ZIEL (SVG-Overlay) */}
        <svg
          className="pointer-events-none absolute inset-0 w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          data-testid="mini-matrix-diagonal-line"
        >
          <line
            x1={ichPos.x}
            y1={ichPos.y}
            x2={zielPos.x}
            y2={zielPos.y}
            stroke="#1F2937"
            strokeWidth="0.8"
            strokeDasharray="2 2"
            opacity="0.7"
          />
        </svg>

        {/* ICH-Punkt (Forest) */}
        <div
          data-testid="mini-matrix-ich"
          className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
          style={{ left: `${ichPos.x}%`, top: `${ichPos.y}%` }}
        >
          <span
            className="block w-4 h-4 rounded-full ring-2 ring-white shadow-md"
            style={{ backgroundColor: ichRole.hex }}
          />
          <span
            className="mt-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/90 shadow-sm"
            style={{ color: ichRole.hex }}
          >
            Ich
          </span>
        </div>

        {/* ZIEL-Punkt (Crimson) */}
        <div
          data-testid="mini-matrix-ziel"
          className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
          style={{ left: `${zielPos.x}%`, top: `${zielPos.y}%` }}
        >
          <span
            className="block w-4 h-4 rounded-full ring-2 ring-white shadow-md"
            style={{ backgroundColor: zielRole.hex }}
          />
          <span
            className="mt-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/90 shadow-sm"
            style={{ color: zielRole.hex }}
          >
            {FOKUSTYP_LABEL[targetSlug]}
          </span>
        </div>
      </div>
    </div>
  );
}

export default MiniMatrix;
